// conflict-apply-core.js — the EDITOR-FREE decision core of the conflict floor.
//
// Split out of conflict-apply.js (Batch 5 Stage A). This module classifies an
// inbound remote update and performs the DURABLE-STORAGE half of acting on it —
// the 3-way auto-merge attempt and the conflicted-copy fork — using ONLY the
// passed NoteStorage handle (`NS`). It imports NOTHING from editor/, ui/, or
// core/app-state: the editor/baseline/banner side-effects live in the thin
// shells in conflict-apply.js, which act on the CoreResult this returns. That
// import-freedom is enforced by web/tests/layering-boundary.test.js so a
// background (non-open-note) path can reuse the fork-floor without dragging in
// the editor.
//
// THE FORK FLOOR is enforced HERE and only here: the ONLY path that writes
// `merged` over a note is gated by merge3 reporting zero conflicts AND a
// structurally-sound result with calendar-event lines guarded; every decline,
// deny-list hit, or throw falls through to the conflicted-copy fork. Both shells
// reach a merge/fork only through this core, so neither can bypass the floor.

import { EVENT_LINE_RE, EVENTS_HEADING } from '../utils/calendar-format.js';
import type { ApplyInboundConflictArgs } from './conflict-detect.js';
import { conflictedCopyName, isDenyListed, resolveInboundConflict } from './conflict-detect.js';

// '## Events'-body content guard, applied ONLY in the executor (it needs the
// bytes the pure classifier doesn't have). A note carrying a '## Events'
// heading holds calendar-managed event lines; a byte-imperfect merge of those
// would desync the calendar, so we never auto-merge such content even if its
// NAME wasn't deny-listed. Heading match is whitespace-tolerant (trim) and
// line-anchored, mirroring calendar-sync's own EVENTS_HEADING scan.
/**
 * @param {any} text
 * @returns {boolean} true if the text carries a '## Events' heading.
 */
function hasEventsSection(text: any) {
  if (typeof text !== 'string') return false;
  for (const line of text.split('\n')) {
    if (line.trim() === EVENTS_HEADING) return true;
  }
  return false;
}

// Extract calendar-event lines for merge3's structural-soundness check. An event
// line is one containing '> ' followed by a six-digit date token — the exact
// shape calendar-sync.js uses to re-identify its managed lines (/>\s+\d{6}\b/).
// We union the lines present on BOTH sides so isStructurallySound can flag a
// merge that dropped or duplicated any event line either side carried.
// EVENT_LINE_RE is imported from utils/calendar-format.js (the single source of
// truth shared with calendar-sync + conflict-detect).
/**
 * @param {...any} texts
 * @returns {string[]} the union of calendar-event lines across the inputs.
 */
function extractEventLines(...texts: any[]) {
  const out = [];
  const seen = new Set();
  for (const text of texts) {
    if (typeof text !== 'string') continue;
    for (const line of text.split('\n')) {
      if (EVENT_LINE_RE.test(line) && !seen.has(line)) {
        seen.add(line);
        out.push(line);
      }
    }
  }
  return out;
}

// Merge-engine loader. Production resolves the engine via a LAZY dynamic import
// so merge3.js never lands in the main bundle and is only paid for when a real
// auto-mergeable conflict occurs. It is indirected through this swappable
// binding purely as a TEST SEAM: Jest's CJS VM cannot service a native dynamic
// import (it throws "without --experimental-vm-modules"), so the merge suite
// injects the real engine via setMergeEngineLoaderForTest. In production the
// default below is untouched — the dynamic import is exactly what ships.
let _loadMergeEngine: any = () => import('./merge3.js');

// TEST-ONLY: override the merge-engine loader (and restore by passing null).
// Never called by production code.
/**
 * @param {any} [fn] - a loader returning the merge engine; null restores default.
 * @returns {void}
 */
export function setMergeEngineLoaderForTest(fn: any) {
  _loadMergeEngine = fn || (() => import('./merge3.js'));
}

/**
 * Classify an inbound remote update and perform its STORAGE side (auto-merge
 * write / fork sidecar) via `NS`. Returns a CoreResult the caller's shell acts
 * on — this function never touches the editor, UI, or app-state.
 *
 * @param {object} args  — see applyInboundConflict in conflict-apply.js.
 * @returns {Promise<any>} CoreResult: one of
 *   { action:'ignore' }
 *   { action:'fastForward', remote }       shell replaces editor + advances baselines
 *   { action:'rebase', remote }            shell advances baselines only
 *   { action:'merge', merged, staleAborted? }  core ALREADY wrote NS.setNote(name,merged)
 *   { action:'fork', forkName, remote, wroteFork, staleAborted? }  core wrote the sidecar
 */
export async function applyInboundConflictCore({
  name,
  base,
  local,
  remote,
  saved,
  hasUnsavedEdits,
  NS,
  recheckGen,
}: ApplyInboundConflictArgs) {
  const stale = typeof recheckGen === 'function' ? recheckGen : () => false;

  const decision = resolveInboundConflict({
    name,
    base,
    local,
    remote,
    saved,
    hasUnsavedEdits,
  });

  if (decision.action === 'ignore') return { action: 'ignore' };
  if (decision.action === 'fastForward') return { action: 'fastForward', remote };
  if (decision.action === 'rebase') return { action: 'rebase', remote };

  // ── auto-merge attempt (ahead of the fork) ─────────────────────────────
  // True divergence the classifier flagged as mergeable (base != null && not
  // deny-listed by name). Try a provably-safe 3-way merge BEFORE forking. The
  // whole attempt is wrapped so the floor (fork) is the fallback for EVERY
  // decline or throw — we never write a non-clean / non-sound merge.
  if (decision.mergeable) {
    try {
      // Lazy dynamic import → merge3.js stays out of the main bundle and is
      // only paid for when a real auto-mergeable conflict actually occurs.
      const { merge3, isStructurallySound } = await _loadMergeEngine();

      // Re-confirm the durable base is still present (the classifier saw a
      // non-null in-memory base; this is the authoritative on-disk one). The
      // merge MUST use this durable base, not the possibly-staler arg `base`.
      // Read via the canonical window.NoteStorage (the encrypting + workspace-
      // namespaced wrap) — DELIBERATELY independent of the passed `NS`, so the
      // authoritative on-disk base re-read uses the canonical store regardless
      // of the handle the caller supplied; NOT the inner PowerSyncNoteStorage,
      // since the stored base is ciphertext and decrypts through the wrap.
      const noteStorage = typeof window !== 'undefined' ? window.NoteStorage : null;
      const mergeBase =
        noteStorage && typeof noteStorage.getMergeBase === 'function'
          ? await noteStorage.getMergeBase(name)
          : null;

      if (mergeBase == null) throw new Error('no durable base');

      // Content-level deny-list: never auto-merge calendar-managed content. The
      // name gate already ran in the classifier; this is the '## Events'-body
      // check that needs the actual bytes, so it lives here. A '## Events'
      // section on ANY of base/local/remote disqualifies the merge.
      if (
        isDenyListed(name) ||
        hasEventsSection(mergeBase) ||
        hasEventsSection(local) ||
        hasEventsSection(remote)
      ) {
        throw new Error('deny-listed content');
      }

      const { merged, conflicts } = merge3(mergeBase, local, remote);
      const originalEventLines = extractEventLines(local, remote);
      const sound = isStructurallySound(merged, { originalEventLines });

      if (
        Array.isArray(conflicts) &&
        conflicts.length === 0 &&
        sound &&
        typeof merged === 'string'
      ) {
        // Provably safe. Re-check generation right before the write — the user
        // may have navigated away during the async import/base read. On stale we
        // return the merged text but DON'T write; the shell skips its editor
        // mutation on staleAborted, matching the pre-split behaviour.
        if (stale()) return { action: 'merge', merged, staleAborted: true };
        // Persist the merged text so it uploads as the converged version. With
        // all three baselines advanced to `merged` (the shell), a re-delivery of
        // either side's pre-merge content is an inbound divergence resolved
        // against the merged base — two devices that merge the same base+sides
        // converge.
        await NS.setNote(name, merged);
        return { action: 'merge', merged };
      }
      // Non-clean or not sound → fall through to the fork below (no write).
    } catch (e: any) {
      // Any decline/throw (import failure, missing base, deny-list, merge
      // error) → the conservative fork remains the fallback.
      console.warn('[conflict] auto-merge declined, forking:', e?.message);
    }
  }

  // ── fork ──────────────────────────────────────────────────────────────
  // True divergence. Keep BOTH sides: the caller keeps local (which keeps
  // uploading via the normal autosave/LWW path because the shell deliberately
  // does NOT setLastSavedContent), and the remote lands as a deterministic
  // sidecar copy.
  if (stale()) {
    return {
      action: 'fork',
      forkName: decision.forkName,
      remote,
      wroteFork: false,
      staleAborted: true,
    };
  }

  const forkName = decision.forkName;
  let wroteFork = false;
  try {
    // Idempotent existence check: if a note named forkName already holds this
    // exact content, don't rewrite it. forkName is derived from hash(remote), so
    // a matching existing copy means another device (or an earlier echo) already
    // forked the same conflict — writing again would be a no-op churn.
    const existing = await NS.getNote(forkName);
    if (existing !== remote) {
      await NS.setNote(forkName, remote);
      wroteFork = true;
    }
  } catch (e: any) {
    // If the sidecar write fails we still keep local intact; the shell surfaces
    // the banner so the user isn't left unaware (the remote content is preserved
    // in the pending-conflict entry for the [Use theirs] action).
    console.error('[conflict] sidecar write failed:', e);
  }

  return { action: 'fork', forkName, remote, wroteFork };
}

// Advance the durable merge base to `content` (best-effort). Writes through the
// encrypting window.NoteStorage wrap via the passed NS handle, so the base stays
// ciphertext at rest. Convergence is ALSO covered by the sweep's plaintext
// snapshot, so a failed base write is non-fatal.
async function _advanceMergeBase(NS: any, name: any, content: any) {
  if (NS && typeof NS.setMergeBase === 'function') {
    try {
      await NS.setMergeBase(name, content);
    } catch {
      /* base advance is best-effort */
    }
  }
}

/**
 * FORK-ONLY background resolver — for a note that changed in a sync batch but is
 * NOT the open note. There is no editor and no human watching, so we deliberately
 * NEVER run a 3-way text merge (which could silently fold un-attributable peer
 * content from a shared workspace). We only ACCEPT the remote (when nothing local
 * is at risk) or FORK (preserve this device's pre-sync content as a deterministic
 * conflicted-copy sidecar). Both sides are always kept; nothing local is ever
 * overwritten without being preserved as a sidecar.
 *
 * @param {object} args
 * @param {string}  args.name      note name (the caller has already excluded the
 *        open note + hidden/deny-listed names).
 * @param {?string} args.localPre  this device's content BEFORE the sync (the
 *        pre-LWW local side); null/undefined when the note is new to this device.
 * @param {string}  args.remote    the post-LWW content now in storage.
 * @param {?string} args.base      the durable merge base (or null when absent /
 *        decrypt-lost).
 * @param {any}     args.NS        the encrypting NoteStorage handle (window.NoteStorage).
 * @returns {Promise<{ action:'skip'|'accept'|'fork', forkName?:string, wroteFork?:boolean }>}
 */
export async function applyBackgroundConflict({ name, localPre, remote, base, NS }: any) {
  // No real change to this device's view of the note.
  if (localPre === remote) return { action: 'skip' };

  // New-to-this-device (added remotely → no local copy to lose) OR a clean
  // forward (local never diverged from the base) → adopt remote. Advance the
  // durable base so a future open / re-delivery sees remote as the ancestor.
  if (localPre == null || (base != null && localPre === base)) {
    await _advanceMergeBase(NS, name, remote);
    return { action: 'accept' };
  }

  // Local diverged AND remote diverged: LWW already absorbed `remote` under
  // `name`, overwriting this device's local edit. Preserve the local edit as a
  // deterministic conflicted-copy sidecar (hash(localPre) → idempotent, so two
  // devices converge on the same copy and a re-run can't duplicate it).
  const forkName = conflictedCopyName(name, localPre);
  let wroteFork = false;
  try {
    const existing = await NS.getNote(forkName);
    if (existing !== localPre) {
      await NS.setNote(forkName, localPre);
      wroteFork = true;
    }
  } catch (e: any) {
    // Log the error only — never the note name/content (Batch-2 redaction).
    console.error('[conflict] background sidecar write failed:', e);
  }
  await _advanceMergeBase(NS, name, remote);
  return { action: 'fork', forkName, wroteFork };
}
